import React, { useEffect, useMemo, useState } from 'react';
import { styled } from '@mui/material/styles';
import Drawer from '@mui/material/Drawer';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import CheckIcon from '@mui/icons-material/Check';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import VerifiedIcon from '@mui/icons-material/Verified';
import FilterListIcon from '@mui/icons-material/FilterList';
import ErrorIcon from '@mui/icons-material/Error';
import { useAppDispatch, useAppSelector } from '../../../app/hooks';
import {
  toggleStatusFilter,
  setQcOnlyFilter,
  toggleIncompleteOnlyFilter,
  toggleTagFilter,
  toggleSpAnswerFilter,
  toggleAssessorAnswerFilter,
  clearStatusFilters,
} from '../../../app/store/assessmentWorkspaceSlice';
import { QC_ISSUE_STATUS_LABELS } from '../../../interfaces/qcIssue.interface';
import {
  OrderedQuestionEntry,
  NavigatorQuestionRow,
  WorkspaceCapabilities,
  groupEntriesForNavigator,
  navigatorSearchMatch,
  questionMatchesFilters,
  hasActiveFilters,
  isQuestionSaved,
  getSpOverallLabel,
  ANSWER_FILTER_OPTIONS,
  QC_GROUP_ORDER,
  tagsByQuestionFrom,
} from './workspaceDerivations';
import { CURATED_TAGS, tagLabel, tagTheme } from './TagInput';
import { QC_STATUS_RGB, ACCENT_BLUE, ACCENT_BLUE_DARK, SUCCESS_GREEN, ERROR_RED } from './qcTheme';

// Docked questionnaire navigator — the map stays in view beside the workspace (docked rail, not the modal drawer).
const Rail = styled('aside')({
  width: '475px',
  flex: 'none',
  alignSelf: 'flex-start',
  position: 'sticky',
  top: '16px',
  maxHeight: 'calc(100vh - 32px)',
  display: 'flex',
  flexDirection: 'column',
  minHeight: 0,
  background: '#fff',
  borderRadius: '4px',
  boxShadow: '0 2px 1px -1px rgba(0,0,0,0.2),0 1px 1px 0 rgba(0,0,0,0.14),0 1px 3px 0 rgba(0,0,0,0.12)',
});

const Header = styled('div')({
  padding: '16px 20px 10px',
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
});

const HeaderTitle = styled('span')({
  display: 'inline-flex',
  alignItems: 'center',
  gap: '8px',
  fontSize: '1.15rem',
  lineHeight: 1.2,
  fontWeight: 600,
  color: 'rgba(0,0,0,0.87)',
  minWidth: 0,
  '& .MuiSvgIcon-root': {
    fontSize: '18px',
    color: ACCENT_BLUE_DARK,
  },
});

// Renders as part of the title: same size, weight, and colour.
const VersionLabel = styled('span')({
  fontSize: '1.15rem',
  lineHeight: 1.2,
  fontWeight: 600,
  color: 'rgba(0,0,0,0.87)',
  flex: 'none',
});

const SearchWrap = styled('div')({
  position: 'relative',
  padding: '0 20px 12px',
});

const SearchInput = styled('input')({
  width: '100%',
  boxSizing: 'border-box',
  border: '1px solid rgba(0,0,0,0.23)',
  borderRadius: '4px',
  padding: '9px 34px 9px 38px',
  fontFamily: 'Roboto, sans-serif',
  fontSize: '0.95rem',
  color: 'rgba(0,0,0,0.87)',
  background: '#fff',
  outline: 'none',
  '&:focus': {
    borderColor: ACCENT_BLUE,
    boxShadow: '0 0 0 2px rgba(25,118,210,0.15)',
  },
});

const SearchIconWrap = styled(SearchIcon)({
  position: 'absolute',
  left: '32px',
  top: '9px',
  fontSize: '20px',
  color: 'rgba(0,0,0,0.42)',
});

const SearchClear = styled('button')({
  position: 'absolute',
  right: '30px',
  top: '9px',
  border: 'none',
  background: 'none',
  cursor: 'pointer',
  color: 'rgba(0,0,0,0.45)',
  padding: 0,
  display: 'inline-flex',
});

const FiltersToggle = styled('button')({
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  width: '100%',
  border: 'none',
  background: 'none',
  cursor: 'pointer',
  fontFamily: 'inherit',
  textAlign: 'left',
  padding: '10px 20px',
  borderTop: '1px solid rgba(0,0,0,0.08)',
  fontSize: '0.875rem',
  fontWeight: 500,
  color: 'rgba(0,0,0,0.7)',
  '&:hover': {
    background: '#f3f5fb',
  },
  '& .MuiSvgIcon-root': {
    fontSize: '17px',
    color: 'rgba(0,0,0,0.45)',
  },
});

const FiltersChevron = styled('span')({
  marginLeft: 'auto',
  display: 'inline-flex',
});

const ActiveFilterDot = styled('span')({
  width: '7px',
  height: '7px',
  borderRadius: '50%',
  background: ACCENT_BLUE,
  flex: 'none',
});

// Filter drawer (right rail); Roboto pinned because the portal renders outside WorkspaceRoot.
const DrawerBody = styled('div')({
  width: '380px',
  maxWidth: '90vw',
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  fontFamily: 'Roboto, Helvetica, Arial, sans-serif',
  color: 'rgba(0,0,0,0.87)',
});

const DrawerHead = styled('div')({
  display: 'flex',
  alignItems: 'center',
  gap: '10px',
  padding: '18px 20px 14px',
  borderBottom: '1px solid rgba(0,0,0,0.1)',
  '& > .MuiSvgIcon-root': {
    fontSize: '20px',
    color: ACCENT_BLUE,
  },
});

const DrawerTitle = styled('span')({
  fontSize: '1.05rem',
  fontWeight: 600,
});

const DrawerCount = styled('span')({
  fontSize: '0.8125rem',
  fontWeight: 500,
  color: 'rgba(0,0,0,0.55)',
});

const DrawerClose = styled('button')({
  marginLeft: 'auto',
  border: 'none',
  background: 'none',
  cursor: 'pointer',
  padding: '4px',
  borderRadius: '50%',
  display: 'inline-flex',
  color: 'rgba(0,0,0,0.55)',
  '&:hover': {
    background: 'rgba(0,0,0,0.06)',
    color: 'rgba(0,0,0,0.8)',
  },
});

const DrawerScroll = styled('div')({
  flex: 1,
  overflowY: 'auto',
  padding: '6px 20px 20px',
});

const SectionCaption = styled('p')({
  fontSize: '0.7rem',
  letterSpacing: '1px',
  fontWeight: 600,
  color: ACCENT_BLUE,
  textTransform: 'uppercase',
  margin: '18px 0 10px',
  '&:not(:first-of-type)': {
    borderTop: '1px solid rgba(0,0,0,0.08)',
    paddingTop: '16px',
  },
});

const DrawerFooter = styled('div')({
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
  padding: '14px 20px',
  borderTop: '1px solid rgba(0,0,0,0.1)',
});

const ClearAllLink = styled('button')({
  border: 'none',
  background: 'none',
  padding: 0,
  fontFamily: 'inherit',
  fontSize: '0.8125rem',
  fontWeight: 500,
  color: ACCENT_BLUE,
  cursor: 'pointer',
});

const DoneButton = styled('button')({
  marginLeft: 'auto',
  background: ACCENT_BLUE,
  color: '#fff',
  border: 'none',
  borderRadius: '4px',
  padding: '8px 20px',
  fontSize: '0.8125rem',
  fontWeight: 600,
  letterSpacing: '0.4px',
  textTransform: 'uppercase',
  cursor: 'pointer',
  fontFamily: 'inherit',
  '&:hover': {
    background: ACCENT_BLUE_DARK,
  },
});

// Answer-family accents, matched to the matrix disposition palette.
const ANSWER_FILTER_COLORS: Record<string, string> = {
  'Fully Implemented': SUCCESS_GREEN,
  'Partially Implemented': '#ed6c02',
  'Not Implemented': ERROR_RED,
  'Not Applicable': '#616161',
};

const OptionList = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  gap: '2px',
});

// Full-width option row: dot + label left, count right, check when selected; zero-count rows stay clickable but recede.
const OptionRow = styled('button', {
  shouldForwardProp: prop => !['on', 'muted'].includes(prop as string),
})<{ on: boolean; muted: boolean }>(({ on, muted }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: '10px',
  width: '100%',
  border: 'none',
  borderRadius: '6px',
  padding: '9px 10px',
  background: on ? 'rgba(25,118,210,0.08)' : 'none',
  cursor: 'pointer',
  fontFamily: 'inherit',
  textAlign: 'left',
  opacity: muted && !on ? 0.45 : 1,
  '&:hover': {
    background: on ? 'rgba(25,118,210,0.12)' : '#f3f5fb',
  },
}));

const OptionDot = styled('span', {
  shouldForwardProp: prop => prop !== 'dot',
})<{ dot: string }>(({ dot }) => ({
  width: '10px',
  height: '10px',
  borderRadius: '50%',
  background: dot,
  flex: 'none',
}));

const OptionLabel = styled('span', {
  shouldForwardProp: prop => prop !== 'on',
})<{ on: boolean }>(({ on }) => ({
  flex: 1,
  minWidth: 0,
  fontSize: '0.875rem',
  fontWeight: on ? 600 : 400,
  color: '#424242',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
}));

const OptionCount = styled('span')({
  fontSize: '0.8125rem',
  color: 'rgba(0,0,0,0.45)',
  fontVariantNumeric: 'tabular-nums',
  flex: 'none',
});

const OptionCheck = styled(CheckIcon)({
  fontSize: '16px',
  color: ACCENT_BLUE,
  flex: 'none',
});

const ChipDot = styled('span', {
  shouldForwardProp: prop => prop !== 'dot',
})<{ dot: string }>(({ dot }) => ({
  width: '8px',
  height: '8px',
  borderRadius: '50%',
  background: dot,
  flex: 'none',
}));

const ScrollBody = styled('div')({
  flex: 1,
  overflow: 'auto',
  padding: '4px 0 8px',
  borderTop: '1px solid rgba(0,0,0,0.08)',
});

const DomainRow = styled('button')({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  width: '100%',
  border: 'none',
  background: 'none',
  padding: '10px 16px',
  cursor: 'pointer',
  fontFamily: 'inherit',
  textAlign: 'left',
  '&:hover': {
    background: '#f3f5fb',
  },
});

const DomainTitle = styled('span')({
  fontSize: '1.05rem',
  fontWeight: 500,
  color: '#424242',
  minWidth: 0,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
});

const TopicRow = styled('button')({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  width: '100%',
  border: 'none',
  background: 'none',
  padding: '10px 16px 10px 28px',
  cursor: 'pointer',
  fontFamily: 'inherit',
  textAlign: 'left',
  fontSize: '1.05rem',
  fontWeight: 500,
  color: '#424242',
  '&:hover': {
    background: '#f3f5fb',
  },
});

const BpRow = styled('div')({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  gap: '8px',
  padding: '10px 16px 10px 40px',
  cursor: 'pointer',
  '&:hover': {
    background: '#f3f5fb',
  },
});

const BpTitle = styled('span')({
  fontSize: '1.05rem',
  fontWeight: 400,
  color: '#424242',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  minWidth: 0,
});

const BpCount = styled('span')({
  fontSize: '0.8125rem',
  color: 'rgba(0,0,0,0.6)',
  whiteSpace: 'nowrap',
});

const QuestionRow = styled('div', {
  shouldForwardProp: prop => prop !== 'current',
})<{ current: boolean }>(({ current }) => ({
  display: 'flex',
  gap: '8px',
  alignItems: 'center',
  margin: '0 10px 4px 52px',
  padding: '10px 12px 10px 9px',
  borderRadius: '6px',
  cursor: 'pointer',
  background: current ? '#f1f3f5' : '#f7f8fb',
}));

const RowCheckbox = styled('button', {
  shouldForwardProp: prop => prop !== 'on',
})<{ on: boolean }>(({ on }) => ({
  width: '14px',
  height: '14px',
  borderRadius: '3px',
  flex: 'none',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'pointer',
  border: on ? `1.5px solid ${ACCENT_BLUE}` : '1.5px solid rgba(0,0,0,0.4)',
  background: on ? ACCENT_BLUE : '#fff',
  boxSizing: 'border-box',
  padding: 0,
  color: '#fff',
}));

// Done = a bare green check; unanswered = a quiet hollow circle placeholder.
const DoneMark = styled('span', {
  shouldForwardProp: prop => prop !== 'done',
})<{ done: boolean }>(({ done }) => ({
  width: '16px',
  height: '16px',
  flex: 'none',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: SUCCESS_GREEN,
  ...(done ? {} : {
    width: '12px',
    height: '12px',
    margin: '0 2px',
    borderRadius: '50%',
    border: '1.5px solid rgba(0,0,0,0.28)',
    background: '#fff',
  }),
}));

const RowText = styled('span')({
  fontSize: '1.05rem',
  fontWeight: 400,
  color: '#424242',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  flex: 1,
  minWidth: 0,
});

const RowTrailing = styled('span')({
  display: 'inline-flex',
  alignItems: 'center',
  gap: '6px',
  flex: 'none',
});

const QcBadge = styled('span')({
  display: 'inline-flex',
  alignItems: 'center',
  gap: '3px',
  background: 'rgba(211,47,47,0.1)',
  color: ERROR_RED,
  borderRadius: '9px',
  padding: '1px 7px',
  fontSize: '0.7rem',
  fontWeight: 700,
  '& .MuiSvgIcon-root': {
    fontSize: '13px',
  },
});

// Typeahead panel under the search input; the tree behind stays the hierarchical view pruned to the same matches.
const SearchDropdown = styled('div')({
  position: 'absolute',
  top: '44px',
  left: '20px',
  right: '20px',
  zIndex: 40,
  background: '#fff',
  border: '1px solid rgba(0,0,0,0.12)',
  borderRadius: '8px',
  boxShadow: '0 8px 24px rgba(0,0,0,0.18)',
  maxHeight: '320px',
  overflowY: 'auto',
});

const ResultRow = styled('div')({
  padding: '10px 20px',
  cursor: 'pointer',
  '&:hover': {
    background: '#f3f5fb',
  },
});

const ResultLabel = styled('div')({
  fontSize: '0.95rem',
  fontWeight: 600,
  color: '#424242',
});

const ResultText = styled('div')({
  fontSize: '0.875rem',
  color: 'rgba(0,0,0,0.6)',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  '& strong': {
    fontWeight: 700,
    color: 'rgba(0,0,0,0.87)',
  },
});

// Bolds every case-insensitive occurrence of the query inside a suggestion.
function highlightMatch(text: string, query: string): React.ReactNode {
  const q = query.trim();
  if (!q) return text;
  const lower = text.toLowerCase();
  const lq = q.toLowerCase();
  const nodes: React.ReactNode[] = [];
  let start = 0;
  let idx = lower.indexOf(lq, start);
  while (idx !== -1) {
    if (idx > start) nodes.push(text.slice(start, idx));
    nodes.push(<strong key={idx}>{text.slice(idx, idx + q.length)}</strong>);
    start = idx + q.length;
    idx = lower.indexOf(lq, start);
  }
  nodes.push(text.slice(start));
  return nodes;
}

const NoResults = styled('div')({
  padding: '32px 20px',
  fontSize: '0.875rem',
  color: 'rgba(0,0,0,0.6)',
  textAlign: 'center',
});

const SelectionFooter = styled('div')({
  display: 'flex',
  alignItems: 'center',
  gap: '14px',
  padding: '12px 20px',
  borderTop: '1px solid rgba(0,0,0,0.12)',
  background: '#fafbfe',
  borderRadius: '0 0 4px 4px',
});

const SelectedCount = styled('span')({
  fontSize: '0.875rem',
  fontWeight: 600,
  color: '#424242',
});

const ClearLink = styled('button')({
  border: 'none',
  background: 'none',
  padding: 0,
  fontFamily: 'inherit',
  fontSize: '0.8125rem',
  fontWeight: 500,
  color: ACCENT_BLUE,
  cursor: 'pointer',
});

const BulkButton = styled('button')({
  marginLeft: 'auto',
  background: ACCENT_BLUE,
  color: '#fff',
  border: 'none',
  borderRadius: '4px',
  padding: '8px 16px',
  fontSize: '0.8125rem',
  fontWeight: 600,
  letterSpacing: '0.4px',
  textTransform: 'uppercase',
  cursor: 'pointer',
  boxShadow: '0 1px 2px rgba(0,0,0,0.15)',
  fontFamily: 'inherit',
  '&:hover': {
    background: ACCENT_BLUE_DARK,
  },
  '&:disabled': {
    background: 'rgba(0,0,0,0.12)',
    color: 'rgba(0,0,0,0.4)',
    boxShadow: 'none',
    cursor: 'not-allowed',
  },
});

export interface NavigatorRailProps {
  entries: OrderedQuestionEntry[];
  currentQuestionId?: number;
  versionLabel: string;
  capabilities: WorkspaceCapabilities;
  checkedIds: number[];
  onToggleCheck: (questionId: number) => void;
  onSetChecked: (questionIds: number[]) => void;
  onNavigate: (entry: OrderedQuestionEntry) => void;
  onOpenBulk: () => void;
}

export default function NavigatorRail(props: NavigatorRailProps): JSX.Element {
  const {
    entries, currentQuestionId, versionLabel, capabilities,
    checkedIds, onToggleCheck, onSetChecked, onNavigate, onOpenBulk,
  } = props;
  const dispatch = useAppDispatch();
  const {
    qcOnlyFilter, incompleteOnlyFilter, tagFilters, statusFilters,
    spAnswerFilters, assessorAnswerFilters,
    qcIssuesByQuestion,
  } = useAppSelector(state => state.assessmentWorkspace);
  const { assessmentQuestionsStatus, questionStatus } = useAppSelector(state => state.questionnaireAnswer);
  // Checkboxes stay hidden until the user enters bulk-select mode from the footer.
  const [selectMode, setSelectMode] = useState(false);
  const [search, setSearch] = useState('');
  const [searchOpen, setSearchOpen] = useState(false);
  const [filtersOpen, setFiltersOpen] = useState(false);
  // Single-open accordion at every level: null follows the current question's branch; -1 = explicitly all closed.
  const [openDomainId, setOpenDomainId] = useState<number | null>(null);
  const [openTopicId, setOpenTopicId] = useState<number | null>(null);
  // Single-open BP accordion: null follows the current BP; -1 = all closed; navigation resets to follow mode.
  const [openBpId, setOpenBpId] = useState<number | null>(null);
  useEffect(() => {
    setOpenDomainId(null);
    setOpenTopicId(null);
    setOpenBpId(null);
  }, [currentQuestionId]);

  // Search always scans the full questionnaire, independent of active filters.
  const allDomains = useMemo(() => groupEntriesForNavigator(entries), [entries]);
  const allRows = useMemo(
    () => allDomains.flatMap(d => d.topics.flatMap(t => t.bps.flatMap(b => b.rows))),
    [allDomains],
  );

  const tagsByQuestion = useMemo(
    () => tagsByQuestionFrom(assessmentQuestionsStatus),
    [assessmentQuestionsStatus],
  );
  const filters = { statusFilters, tagFilters, spAnswerFilters, assessorAnswerFilters, qcOnlyFilter, incompleteOnlyFilter };
  const filterContext = { qcIssuesByQuestion, tagsByQuestion, assessmentQuestionsStatus, questionStatus };
  const searching = search.trim().length > 0;
  const filtering = !searching && hasActiveFilters(filters);
  // Search and filters both prune the tree; search additionally floats a typeahead dropdown over it.
  const searchMatchedIds = useMemo(() => {
    if (!searching) return null;
    return new Set(allRows.filter(row => navigatorSearchMatch(row, search)).map(row => row.entry.question.id));
  }, [allRows, search, searching]);
  const treeEntries = useMemo(
    () => {
      if (searchMatchedIds) return entries.filter(e => searchMatchedIds.has(e.question.id));
      return hasActiveFilters(filters) ? entries.filter(e => questionMatchesFilters(e.question, filters, filterContext)) : entries;
    },
    [entries, searchMatchedIds, statusFilters, tagFilters, spAnswerFilters, assessorAnswerFilters, qcOnlyFilter, incompleteOnlyFilter, qcIssuesByQuestion, tagsByQuestion, assessmentQuestionsStatus, questionStatus],
  );
  const domains = useMemo(() => groupEntriesForNavigator(treeEntries), [treeEntries]);
  const activeFilterCount = statusFilters.length + tagFilters.length + spAnswerFilters.length
    + assessorAnswerFilters.length + (qcOnlyFilter ? 1 : 0) + (incompleteOnlyFilter ? 1 : 0);

  // Per-facet question counts shown inside the drawer chips.
  const spAnswerCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    ANSWER_FILTER_OPTIONS.forEach(label => { counts[label] = 0; });
    entries.forEach(e => {
      const label = getSpOverallLabel(e.question, questionStatus?.[e.question.id]?.questionAnswer);
      if (counts[label] !== undefined) counts[label] += 1;
    });
    return counts;
  }, [entries, questionStatus]);
  const assessorAnswerCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    ANSWER_FILTER_OPTIONS.forEach(label => { counts[label] = 0; });
    entries.forEach(e => {
      const label = assessmentQuestionsStatus?.[e.question.id]?.answer;
      if (label && counts[label] !== undefined) counts[label] += 1;
    });
    return counts;
  }, [entries, assessmentQuestionsStatus]);
  const incompleteCount = useMemo(
    () => entries.filter(e => !isQuestionSaved(e.question.id, assessmentQuestionsStatus)).length,
    [entries, assessmentQuestionsStatus],
  );
  const hasQcCount = useMemo(
    () => entries.filter(e => (qcIssuesByQuestion[e.question.id] || []).length > 0).length,
    [entries, qcIssuesByQuestion],
  );
  const statusQuestionCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    entries.forEach(e => {
      const statuses = new Set((qcIssuesByQuestion[e.question.id] || []).map(i => i.status));
      statuses.forEach(status => { counts[status] = (counts[status] || 0) + 1; });
    });
    return counts;
  }, [entries, qcIssuesByQuestion]);

  const resultRows = useMemo(() => {
    if (searching) return allRows.filter(row => navigatorSearchMatch(row, search));
    return [];
  }, [allRows, search, searching]);

  const tagUsage = (value: string) => Object.values(tagsByQuestion).filter(tags => tags.includes(value)).length;
  const tagChipNames = useMemo(
    () => CURATED_TAGS.map(t => t.value).sort((a, b) => tagUsage(b) - tagUsage(a)),
    [tagsByQuestion],
  );

  const statusChips = QC_GROUP_ORDER.filter(status => {
    if (status === 'first_pass' && !capabilities.firstPassVisible) return false;
    if (!capabilities.qcIssuesVisible) return false;
    return Object.values(qcIssuesByQuestion).some(issues => issues.some(i => i.status === status));
  });

  // Commit on mousedown — before blur unmounts the panel — then clear search back to the tree; onClick stays as the keyboard/AT fallback.
  const pickSuggestion = (row: NavigatorQuestionRow) => {
    onNavigate(row.entry);
    setSearch('');
    setSearchOpen(false);
  };

  const renderRow = (row: NavigatorQuestionRow) => {
    const questionId = row.entry.question.id;
    const issues = qcIssuesByQuestion[questionId] || [];
    const tags = tagsByQuestion[questionId] || [];
    return (
      <QuestionRow
        key={questionId}
        current={questionId === currentQuestionId}
        role='button'
        onClick={() => onNavigate(row.entry)}
      >
        {selectMode && (
          <RowCheckbox
            on={checkedIds.includes(questionId)}
            aria-label={`Select ${row.entry.question.shortCode || row.entry.question.title}`}
            aria-pressed={checkedIds.includes(questionId)}
            onClick={e => { e.stopPropagation(); onToggleCheck(questionId); }}
          >
            {checkedIds.includes(questionId) && <CheckIcon sx={{ fontSize: 11 }} />}
          </RowCheckbox>
        )}
        <DoneMark done={isQuestionSaved(questionId, assessmentQuestionsStatus)}>
          {isQuestionSaved(questionId, assessmentQuestionsStatus) && <CheckIcon sx={{ fontSize: 16, '& path': { stroke: 'currentColor', strokeWidth: 1.6 } }} />}
        </DoneMark>
        <RowText>{row.entry.question.title}</RowText>
        <RowTrailing>
          {capabilities.qcIssuesVisible && issues.length > 0 && (
            <QcBadge>
              <ErrorIcon />
              {issues.length}
            </QcBadge>
          )}
          {tags.map(tag => <ChipDot key={tag} dot={tagTheme(tag).text} />)}
        </RowTrailing>
      </QuestionRow>
    );
  };

  return (
    <Rail aria-label='Questionnaire navigator'>
      <Header>
        <HeaderTitle>
          <VerifiedIcon />
          TPN Best Practices
        </HeaderTitle>
        <VersionLabel>{versionLabel}</VersionLabel>
      </Header>
      <SearchWrap>
        <SearchIconWrap />
        <SearchInput
          value={search}
          placeholder='Jump to a question, topic, or code…'
          onChange={e => { setSearch(e.target.value); setSearchOpen(true); }}
          onFocus={() => setSearchOpen(true)}
          onBlur={() => setSearchOpen(false)}
          onKeyDown={e => { if (e.key === 'Escape') setSearchOpen(false); }}
        />
        {searching && (
          <SearchClear type='button' aria-label='Clear search' onClick={() => { setSearch(''); setSearchOpen(false); }}>
            <CloseIcon sx={{ fontSize: 18 }} />
          </SearchClear>
        )}
        {searching && searchOpen && (
          <SearchDropdown aria-label='Search suggestions'>
            {resultRows.length === 0 && (
              <NoResults>{`No questions match "${search}".`}</NoResults>
            )}
            {resultRows.map(row => (
              <ResultRow
                key={row.entry.question.id}
                role='button'
                onMouseDown={e => { e.preventDefault(); pickSuggestion(row); }}
                onClick={() => pickSuggestion(row)}
              >
                <ResultLabel>
                  {highlightMatch(`${row.entry.question.shortCode || row.entry.bestPractice.title} · Q${row.qNum}`, search)}
                </ResultLabel>
                <ResultText>{highlightMatch(row.entry.question.title, search)}</ResultText>
              </ResultRow>
            ))}
          </SearchDropdown>
        )}
      </SearchWrap>
      {!searching && (
        <FiltersToggle type='button' aria-haspopup='dialog' aria-expanded={filtersOpen} onClick={() => setFiltersOpen(true)}>
          <FilterListIcon />
          Filters
          {activeFilterCount > 0 && <ActiveFilterDot />}
          {activeFilterCount > 0 && <DrawerCount>{activeFilterCount} active</DrawerCount>}
          <FiltersChevron>
            <ExpandMoreIcon sx={{ transform: 'rotate(-90deg)' }} />
          </FiltersChevron>
        </FiltersToggle>
      )}
      <Drawer anchor='right' open={filtersOpen} onClose={() => setFiltersOpen(false)}>
        <DrawerBody role='dialog' aria-label='Question filters'>
          <DrawerHead>
            <FilterListIcon />
            <DrawerTitle>Filters</DrawerTitle>
            {activeFilterCount > 0 && <DrawerCount>{activeFilterCount} active</DrawerCount>}
            <DrawerClose type='button' aria-label='Close filters' onClick={() => setFiltersOpen(false)}>
              <CloseIcon sx={{ fontSize: 20 }} />
            </DrawerClose>
          </DrawerHead>
          <DrawerScroll>
            <SectionCaption>SP Answer</SectionCaption>
            <OptionList>
              {ANSWER_FILTER_OPTIONS.map(label => {
                const on = spAnswerFilters.includes(label);
                return (
                  <OptionRow key={label} type='button' on={on} muted={!spAnswerCounts[label]} aria-pressed={on} onClick={() => dispatch(toggleSpAnswerFilter(label))}>
                    <OptionDot dot={ANSWER_FILTER_COLORS[label]} />
                    <OptionLabel on={on}>{label}</OptionLabel>
                    {on && <OptionCheck />}
                    <OptionCount>{spAnswerCounts[label]}</OptionCount>
                  </OptionRow>
                );
              })}
            </OptionList>
            <SectionCaption>Assessor Answer</SectionCaption>
            <OptionList>
              {ANSWER_FILTER_OPTIONS.map(label => {
                const on = assessorAnswerFilters.includes(label);
                return (
                  <OptionRow key={label} type='button' on={on} muted={!assessorAnswerCounts[label]} aria-pressed={on} onClick={() => dispatch(toggleAssessorAnswerFilter(label))}>
                    <OptionDot dot={ANSWER_FILTER_COLORS[label]} />
                    <OptionLabel on={on}>{label}</OptionLabel>
                    {on && <OptionCheck />}
                    <OptionCount>{assessorAnswerCounts[label]}</OptionCount>
                  </OptionRow>
                );
              })}
            </OptionList>
            <SectionCaption>Progress</SectionCaption>
            <OptionList>
              <OptionRow type='button' on={incompleteOnlyFilter} muted={!incompleteCount} aria-pressed={incompleteOnlyFilter} onClick={() => dispatch(toggleIncompleteOnlyFilter())}>
                <OptionDot dot='#90a4ae' />
                <OptionLabel on={incompleteOnlyFilter}>Incomplete</OptionLabel>
                {incompleteOnlyFilter && <OptionCheck />}
                <OptionCount>{incompleteCount}</OptionCount>
              </OptionRow>
            </OptionList>
            {capabilities.qcIssuesVisible && (
              <>
                <SectionCaption>QC Review</SectionCaption>
                <OptionList>
                  <OptionRow type='button' on={qcOnlyFilter} muted={!hasQcCount} aria-pressed={qcOnlyFilter} onClick={() => dispatch(setQcOnlyFilter(!qcOnlyFilter))}>
                    <ErrorIcon sx={{ fontSize: 15, color: ERROR_RED, flex: 'none' }} />
                    <OptionLabel on={qcOnlyFilter}>Has QC Issues</OptionLabel>
                    {qcOnlyFilter && <OptionCheck />}
                    <OptionCount>{hasQcCount}</OptionCount>
                  </OptionRow>
                  {statusChips.map(status => {
                    const on = statusFilters.includes(status);
                    return (
                      <OptionRow key={status} type='button' on={on} muted={!statusQuestionCounts[status]} aria-pressed={on} onClick={() => dispatch(toggleStatusFilter(status))}>
                        <OptionDot dot={`rgb(${QC_STATUS_RGB[status]})`} />
                        <OptionLabel on={on}>{status === 'first_pass' ? 'First Pass' : QC_ISSUE_STATUS_LABELS[status]}</OptionLabel>
                        {on && <OptionCheck />}
                        <OptionCount>{statusQuestionCounts[status] || 0}</OptionCount>
                      </OptionRow>
                    );
                  })}
                </OptionList>
              </>
            )}
            {tagChipNames.length > 0 && (
              <>
                <SectionCaption>Tags</SectionCaption>
                <OptionList>
                  {tagChipNames.map(name => {
                    const on = tagFilters.includes(name);
                    const theme = tagTheme(name);
                    return (
                      <OptionRow key={name} type='button' on={on} muted={!tagUsage(name)} aria-pressed={on} onClick={() => dispatch(toggleTagFilter(name))}>
                        <OptionDot dot={theme.text} />
                        <OptionLabel on={on}>{tagLabel(name)}</OptionLabel>
                        {on && <OptionCheck />}
                        <OptionCount>{tagUsage(name)}</OptionCount>
                      </OptionRow>
                    );
                  })}
                </OptionList>
              </>
            )}
          </DrawerScroll>
          <DrawerFooter>
            <ClearAllLink type='button' onClick={() => dispatch(clearStatusFilters())}>Clear all</ClearAllLink>
            <DoneButton type='button' onClick={() => setFiltersOpen(false)}>{`View ${treeEntries.length} result${treeEntries.length === 1 ? '' : 's'}`}</DoneButton>
          </DrawerFooter>
        </DrawerBody>
      </Drawer>
      <ScrollBody>
        {searching && treeEntries.length === 0 && (
          <NoResults>{`No questions match "${search}".`}</NoResults>
        )}
        {!searching && filtering && treeEntries.length === 0 && (
          <NoResults>No questions match the active filters.</NoResults>
        )}
        {domains.map(domainGroup => {
          const domainId = domainGroup.domain.id;
          const domainIsCurrent = domainGroup.topics.some(t => t.bps.some(b => b.rows.some(r => r.entry.question.id === currentQuestionId)));
          // While searching every pruned branch stays open so matches are visible.
          const isDomainOpen = searching || (openDomainId === null ? domainIsCurrent : openDomainId === domainId);
          return (
            <div key={domainId}>
              <DomainRow type='button' onClick={() => setOpenDomainId(isDomainOpen ? -1 : domainId)}>
                <DomainTitle>{domainGroup.domain.title}</DomainTitle>
                {isDomainOpen ? <ExpandLessIcon sx={{ fontSize: 22, color: 'rgba(0,0,0,0.6)' }} /> : <ExpandMoreIcon sx={{ fontSize: 22, color: 'rgba(0,0,0,0.6)' }} />}
              </DomainRow>
              {isDomainOpen && domainGroup.topics.map((topicGroup, topicIndex) => {
                const topicId = topicGroup.topic.id;
                const topicIsCurrent = topicGroup.bps.some(b => b.rows.some(r => r.entry.question.id === currentQuestionId));
                const isTopicOpen = searching || (openTopicId === null ? topicIsCurrent : openTopicId === topicId);
                return (
                  <div key={topicId}>
                    <TopicRow type='button' onClick={() => setOpenTopicId(isTopicOpen ? -1 : topicId)}>
                      {topicGroup.topicNumber ?? topicIndex + 1}. {topicGroup.topic.title}
                      {isTopicOpen ? <ExpandLessIcon sx={{ fontSize: 18, color: 'rgba(0,0,0,0.45)' }} /> : <ExpandMoreIcon sx={{ fontSize: 18, color: 'rgba(0,0,0,0.45)' }} />}
                    </TopicRow>
                    {isTopicOpen && topicGroup.bps.map(bpGroup => {
                      const bpId = bpGroup.bestPractice.id;
                      const bpHasCurrent = bpGroup.rows.some(r => r.entry.question.id === currentQuestionId);
                      const isBpOpen = searching || (openBpId === null ? bpHasCurrent : openBpId === bpId);
                      const bpIds = bpGroup.rows.map(r => r.entry.question.id);
                      const allSelected = bpIds.every(id => checkedIds.includes(id));
                      const doneCount = bpIds.filter(id => isQuestionSaved(id, assessmentQuestionsStatus)).length;
                      return (
                        <div key={bpId}>
                          <BpRow
                            onClick={() => {
                              // Clicking a BP opens it and lands on its first question; the chevron toggles collapse without navigating.
                              setOpenBpId(bpId);
                              if (bpGroup.rows[0]) onNavigate(bpGroup.rows[0].entry);
                            }}
                          >
                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: '10px', minWidth: 0 }}>
                              {selectMode && (
                                <RowCheckbox
                                  on={allSelected}
                                  aria-label={`Select all in ${bpGroup.bestPractice.title}`}
                                  aria-pressed={allSelected}
                                  onClick={e => {
                                    e.stopPropagation();
                                    if (allSelected) onSetChecked(checkedIds.filter(id => !bpIds.includes(id)));
                                    else onSetChecked([...new Set([...checkedIds, ...bpIds])]);
                                  }}
                                >
                                  {allSelected && <CheckIcon sx={{ fontSize: 11 }} />}
                                </RowCheckbox>
                              )}
                              <BpTitle>
                                {bpGroup.bpNumber !== undefined ? `${bpGroup.bpNumber}. ` : ''}
                                {bpGroup.bestPractice.title}
                              </BpTitle>
                            </span>
                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
                              <BpCount>{doneCount}/{bpGroup.rows.length}</BpCount>
                              <span
                                role='button'
                                aria-label={`${isBpOpen ? 'Collapse' : 'Expand'} ${bpGroup.bestPractice.title}`}
                                style={{ display: 'inline-flex' }}
                                onClick={e => { e.stopPropagation(); setOpenBpId(isBpOpen ? -1 : bpId); }}
                              >
                                {isBpOpen ? <ExpandLessIcon sx={{ fontSize: 20, color: 'rgba(0,0,0,0.5)' }} /> : <ExpandMoreIcon sx={{ fontSize: 20, color: 'rgba(0,0,0,0.5)' }} />}
                              </span>
                            </span>
                          </BpRow>
                          {isBpOpen && bpGroup.rows.map(renderRow)}
                        </div>
                      );
                    })}
                  </div>
                );
              })}
            </div>
          );
        })}
      </ScrollBody>
      {capabilities.findingsEditable && (
        <SelectionFooter>
          {selectMode ? (
            <>
              <SelectedCount>{checkedIds.length} selected</SelectedCount>
              <ClearLink type='button' onClick={() => { setSelectMode(false); onSetChecked([]); }}>Cancel</ClearLink>
              <BulkButton type='button' disabled={!checkedIds.length} onClick={onOpenBulk}>Bulk Update</BulkButton>
            </>
          ) : (
            <BulkButton type='button' onClick={() => setSelectMode(true)}>Bulk Update</BulkButton>
          )}
        </SelectionFooter>
      )}
    </Rail>
  );
}
